import XIcon from "@mui/icons-material/Close"
import DeleteIcon from "@mui/icons-material/Delete"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"

import { DialogProps } from "@/dialogs"
import {
  AgeRangeEnum,
  Event,

  EventType,
  ImageOrientationEnum,
} from "@/common/types"

import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import ControlledTextInput from "@/components/controlledInputs/ControlledTextInput"
import GoogleAutoAddress from "@/components/controlledInputs/ControlledGoogleAddress"
import ControlledDatePicker from "@/components/controlledInputs/ControlledDatePIcker"
import ControlledSwitch from "@/components/controlledInputs/ControlledSwitch"
import FileUploader from "@/components/FileUploader"
import ControlledRadioGroup from "@/components/controlledInputs/ControlledRadioGroup"
import useListEventTypes from "@/hooks/api/events/useListEventTypes"
import ControlledCheckboxGroup from "@/components/controlledInputs/ControlledCheckBoxGroup"
import useEditEvent from "@/hooks/api/events/useEditEvent"
import { useEffect, useState } from "react"
import { ImageType, s3, warmS3Credentials } from "@/common/globals"
import { PutObjectCommand } from "@aws-sdk/client-s3"

const editEventFormData = z
  .object({
    event_id: z.number().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    type: z.array(z.number()).optional(),
    address: z.string().optional(),
    location: z.string().optional(),
    event_start_timestamp: z.date().optional(),
    event_end_timestamp: z.date().optional(),
    button_label: z.string().optional(),
    position: z
      .object({
        lat: z.number(),
        long: z.number(),
      })
      .optional(),
    price: z.string().optional(),
    action_url: z.string().optional(),
    pet_friendly: z.boolean().optional(),
    age_range: z.nativeEnum(AgeRangeEnum).optional(),
    images: z
      .array(
        z.object({
          file: z.object({
            name: z.string(),
            path: z.string().optional(),
            lastModified: z.number().optional(),
            size: z.number().optional(),
            type: z.string().optional(),
          }),
          orientation: z.nativeEnum(ImageOrientationEnum),
          order: z.number(),
        })
      )
      .optional(),
  })
  .refine(
    (data) => {
      if (!data.event_end_timestamp || !data.event_start_timestamp) {
        return true
      }
      return data.event_start_timestamp <= data.event_end_timestamp
    },
    {
      path: ["event_end_timestamp"],
      message: "End date must be after start date.",
    }
  )

export type EditEventFormDataType = z.infer<typeof editEventFormData>

interface EditEventDialogProps extends DialogProps {
  event: Event
}

function EditEventDialog({ isOpen, closeDialog, event }: EditEventDialogProps) {
  const [horizontalFiles, setHorizontalFiles] = useState<ImageType[]>([])
  const [verticalFiles, setVerticalFiles] = useState<ImageType[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [imagesToRemove, setImagesToRemove] = useState<number[]>([]) // Track event_image_ids to delete

  function handleClose() {
    setSubmitError(null)
    setHorizontalFiles([])
    setVerticalFiles([])
    setImagesToRemove([])
    closeDialog()
  }
  const { data: eventTypes, isLoading: eventTypesLoading } = useListEventTypes()
  const { mutateAsync: editEvent } = useEditEvent(event.event_id)

  const defaultValues: EditEventFormDataType = {
    title: event.title,
    description: event.description,
    type: event.event_types
      ? event.event_types.map((eventType) => eventType.event_type_id)
      : [],
    address: event.address,
    location: event.location_title,
    event_start_timestamp: new Date(event.event_start_timestamp),
    event_end_timestamp: event.event_end_timestamp
      ? new Date(event.event_end_timestamp)
      : undefined,
    button_label: event.button_label,
    position: { lat: event.position.x, long: event.position.y },
    price: event.price,
    action_url: event.action_url,
    pet_friendly: Boolean(event.pet_friendly),
    age_range: event.age_range,
    // images: event.event_images,
  }

  const {
    handleSubmit,
    // reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<EditEventFormDataType>({
    resolver: zodResolver(editEventFormData),
    defaultValues: defaultValues,
  })

  const handleSetValue = (value: string) => {
    setValue("address", value)
  }

  const handleRemoveExistingImage = (imageId: number) => {
    setImagesToRemove(prev => [...prev, imageId])
  }

  const handleRestoreExistingImage = (imageId: number) => {
    setImagesToRemove(prev => prev.filter(id => id !== imageId))
  }

  // Filter existing images to show only those not marked for removal
  const existingImages = event.event_images?.filter(img => !imagesToRemove.includes(img.event_image_id)) || []
  const horizontalExistingImages = existingImages.filter(img => img.orientation === ImageOrientationEnum.HORIZONTAL)
  const verticalExistingImages = existingImages.filter(img => img.orientation === ImageOrientationEnum.VERTICAL)
  const removedImages = event.event_images?.filter(img => imagesToRemove.includes(img.event_image_id)) || []

  const submitEvent = async (data: EditEventFormDataType) => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      // if (data.address !== event.address) {
      const geocoder = new window.google.maps.Geocoder()
      await geocoder.geocode({ address: data.address }, (results, status) => {
        if (status === "OK" && results) {
          console.log(results[0].geometry.location)
          const lat = results[0].geometry.location.lat()
          const lng = results[0].geometry.location.lng()
          data["position"] = { lat: lat, long: lng }
        } else {
          console.error("Geocoding failed:", status)
        }
      })
      // }

      // Ensure S3 credentials are ready before uploading
      await warmS3Credentials()

      // Add images to data if any were uploaded
      const allImages = horizontalFiles.concat(verticalFiles)
      if (allImages.length > 0) {
        // Convert File objects to match backend schema
        const validImages = allImages
          .filter(
            (image) => image.file && image.file.name && image.orientation && image.order
          )
          .map((image) => ({
            file: {
              name: image.file.name,
              path: image.file.path,
              lastModified: image.file.lastModified,
              size: image.file.size,
              type: image.file.type,
            },
            orientation: image.orientation,
            order: image.order,
          }))

        if (validImages.length > 0) {
          data.images = validImages
        }
      }

      await editEvent(data).then(async (res) => {
        console.log(res)

        // Upload images to S3 if any were added
        if (allImages.length > 0) {
          for (const file of allImages) {
            const params = {
              Bucket: `${import.meta.env.VITE_AWS_EVENT_BUCKET}`,
              Key: `${event.event_id}/${file.file.name}`,
              Body: file.file,
              ContentType: file.file.type,
            }

            try {
              await s3.send(new PutObjectCommand(params))
            } catch (err) {
              console.error("There was a problem uploading images", err)
            }
          }
        }

        setHorizontalFiles([])
        setVerticalFiles([])
        closeDialog()
      })
    } catch (error) {
      console.error("Error updating event:", error)
      setSubmitError("Failed to update event. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    console.log("errors", errors)
  }, [errors])

  const ageRangeOptions = Object.values(AgeRangeEnum).map((type) => ({
    label: type
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char: string) => char.toUpperCase()),
    value: type,
  }))

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        minWidth={"500px"}
        pl={3}
        pr={1}
        py={1}
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography>Edit Event</Typography>
        <Box>
          <IconButton color="inherit" onClick={handleClose}>
            <XIcon />
          </IconButton>
        </Box>
      </Stack>
      <DialogContent
        sx={{
          py: 2,
        }}
      >
        <form onSubmit={handleSubmit(submitEvent)}>
          {!eventTypesLoading && eventTypes && (
            <Stack spacing={1}>
              <ControlledCheckboxGroup
                control={control}
                name="type"
                label="Event Type"
                options={eventTypes.eventTypes
                  .filter(
                    (element: EventType) =>
                      !["attractions", "favorites"].includes(
                        element.machine_name
                      )
                  )
                  .map((element: EventType) => {
                    return {
                      label: element.human_name,
                      value: element.event_type_id,
                    }
                  })}
              />
              <ControlledTextInput
                variant="outlined"
                control={control}
                name="title"
                label="Event title"
                type="text"
              />
              <ControlledTextInput
                control={control}
                name="description"
                label="Event description"
                type="text"
              />
              <GoogleAutoAddress
                control={control}
                name="address"
                label="Event Address"
                type="text"
                setValue={handleSetValue}
              />
              <ControlledTextInput
                control={control}
                name="location"
                label="Location Title"
                type="text"
              />
              <ControlledDatePicker
                label="Start Date and Time"
                control={control}
                name="event_start_timestamp"
              />
              <ControlledDatePicker
                label="End Date and Time (optional)"
                control={control}
                name="event_end_timestamp"
              />
              <ControlledTextInput
                control={control}
                name="price"
                label="Price"
                type="text"
              />
              <ControlledTextInput
                control={control}
                name="button_label"
                label="Button Label"
                type="text"
              />
              <ControlledTextInput
                control={control}
                name="action_url"
                label="Action Url"
              />
              <ControlledSwitch
                control={control}
                name="pet_friendly"
                label="Pet Friendly"
              />
              <ControlledRadioGroup
                control={control}
                name="age_range"
                label="Age Range"
                options={ageRangeOptions}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Event Images
                </Typography>

                {/* Existing Images Section */}
                {(event.event_images && event.event_images.length > 0) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Current Images
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Horizontal ({horizontalExistingImages.length})
                        </Typography>
                        <Stack spacing={1}>
                          {horizontalExistingImages.map((image) => (
                            <Card key={image.event_image_id} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                              <CardMedia
                                component="img"
                                sx={{ width: 60, height: 40, objectFit: 'cover', mr: 1 }}
                                image={image.image_url}
                                alt={`Event image ${image.image_order}`}
                              />
                              <CardContent sx={{ flex: 1, py: 0, px: 1, '&:last-child': { pb: 0 } }}>
                                <Typography variant="caption">
                                  Order: {image.image_order}
                                </Typography>
                              </CardContent>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveExistingImage(image.event_image_id)}
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Vertical ({verticalExistingImages.length})
                        </Typography>
                        <Stack spacing={1}>
                          {verticalExistingImages.map((image) => (
                            <Card key={image.event_image_id} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                              <CardMedia
                                component="img"
                                sx={{ width: 40, height: 60, objectFit: 'cover', mr: 1 }}
                                image={image.image_url}
                                alt={`Event image ${image.image_order}`}
                              />
                              <CardContent sx={{ flex: 1, py: 0, px: 1, '&:last-child': { pb: 0 } }}>
                                <Typography variant="caption">
                                  Order: {image.image_order}
                                </Typography>
                              </CardContent>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveExistingImage(image.event_image_id)}
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    </Stack>

                    {/* Removed Images (with restore option) */}
                    {removedImages.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Images marked for removal ({removedImages.length})
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" spacing={1}>
                          {removedImages.map((image) => (
                            <Card key={image.event_image_id} sx={{ display: 'flex', alignItems: 'center', p: 1, opacity: 0.6 }}>
                              <CardMedia
                                component="img"
                                sx={{
                                  width: image.orientation === ImageOrientationEnum.HORIZONTAL ? 60 : 40,
                                  height: image.orientation === ImageOrientationEnum.HORIZONTAL ? 40 : 60,
                                  objectFit: 'cover',
                                  mr: 1,
                                  filter: 'grayscale(100%)'
                                }}
                                image={image.image_url}
                                alt={`Removed image ${image.image_order}`}
                              />
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleRestoreExistingImage(image.event_image_id)}
                                sx={{ ml: 1 }}
                              >
                                Restore
                              </Button>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Upload New Images Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Add New Images (optional)
                  </Typography>
                  <FileUploader
                    horizontalFiles={horizontalFiles}
                    setHorizontalFiles={setHorizontalFiles}
                    verticalFiles={verticalFiles}
                    setVerticalFiles={setVerticalFiles}
                  />
                </Box>
              </Box>
            </Stack>
          )}
          {submitError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {submitError}
            </Alert>
          )}
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              Please fix the following errors:
              <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                {Object.entries(errors).map(([key, error]) => (
                  <li key={key}>{error?.message}</li>
                ))}
              </ul>
            </Alert>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={5}>
            <Button variant="text" color="inherit" onClick={closeDialog} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditEventDialog
